import CategoryBadge from './CategoryBadge';

export interface Message {
  id: string;
  sender_name: string;
  content: string;
  timestamp: string;
  media_type?: string;
}

interface MessageBubbleProps {
  message: Message;
  isHighlighted?: boolean;
  signalBadge?: string;
}

const senderColors = [
  'text-blue-700',
  'text-green-700',
  'text-purple-700',
  'text-rose-700',
  'text-teal-700',
  'text-amber-700',
  'text-indigo-700',
  'text-pink-700',
];

function hashColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return senderColors[Math.abs(hash) % senderColors.length];
}

function formatTime(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) {
    // Try parsing as numeric timestamp if ISO failed
    const num = Number(dateStr);
    if (!isNaN(num)) {
      const d = new Date(num > 1e12 ? num : num * 1000);
      if (!isNaN(d.getTime())) {
        return d.toLocaleTimeString(undefined, {
          hour: '2-digit',
          minute: '2-digit',
        });
      }
    }
    return ''; // Return empty string instead of "Invalid Date"
  }
  return date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });
}

const mediaIcons: Record<string, string> = {
  image: '🖼️',
  video: '🎥',
  audio: '🎵',
  document: '📄',
  sticker: '🏷️',
  location: '📍',
  contact: '👤',
};

export default function MessageBubble({
  message,
  isHighlighted = false,
  signalBadge,
}: MessageBubbleProps) {
  const nameColor = hashColor(message.sender_name);

  return (
    <div
      className={`relative rounded-lg px-3 py-2 max-w-[85%] ${
        isHighlighted
          ? 'bg-yellow-50 border border-yellow-300'
          : 'bg-white border border-gray-200'
      }`}
    >
      {/* Signal badge overlay */}
      {signalBadge && (
        <div className="absolute -top-2 ltr:-right-2 rtl:-left-2">
          <CategoryBadge category={signalBadge} />
        </div>
      )}

      {/* Sender name */}
      <p className={`text-xs font-semibold mb-0.5 ${nameColor}`}>
        {message.sender_name}
      </p>

      {/* Media indicator */}
      {message.media_type && message.media_type !== 'text' && (
        <span className="inline-flex items-center gap-1 text-xs text-gray-500 mb-1">
          {mediaIcons[message.media_type] ?? '📎'}{' '}
          <span className="capitalize">{message.media_type}</span>
        </span>
      )}

      {/* Content */}
      <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap break-words">
        {message.content}
      </p>

      {/* Timestamp */}
      <p className="text-[10px] text-gray-400 text-end mt-1">
        {formatTime(message.timestamp)}
      </p>
    </div>
  );
}
